import { z } from "zod";

/**
 * The platform's runtime environment contract. Every var the platform reads
 * at runtime is declared here so that a missing or malformed value surfaces
 * as a clear boot-time failure rather than a `null`/`undefined` later in
 * feature code.
 *
 * Required vars are listed in `docs/environment.md` and mirrored in
 * `.env.example`. Two CLI/test paths read `process.env` directly by design;
 * the same doc enumerates them.
 */
export const envSchema = z.object({
  // Anthropic — consumed only inside packages/messaging/.
  ANTHROPIC_API_KEY: z.string().min(1),

  // External tool APIs.
  //
  // `BRAVE_SEARCH_API_KEY` is reserved — no agent currently consumes it
  // (the curator's web research uses Anthropic-native `web_search`).
  // Kept declared but `.optional()` so operators are not forced to
  // provision a placeholder secret to satisfy the boot check. Will be
  // promoted back to required if a future agent adds the Brave Search
  // tool. See `docs/environment.md` and issue #168.
  BRAVE_SEARCH_API_KEY: z.string().min(1).optional(),
  RESEND_API_KEY: z.string().min(1),

  // Google service-account credentials. Set exactly one — the
  // `.refine()` chained at the bottom of this schema enforces
  // at-least-one. Both are optional in the type system because the
  // local-dev flow uses the file path while prod carries the
  // key inside an env var:
  //
  // - **`GOOGLE_SERVICE_ACCOUNT_PATH`** — absolute filesystem path to
  //   the SA JSON key. The local-dev shape; the file lives outside
  //   the repo (see CLAUDE.md / pre-commit Rule 2).
  // - **`GOOGLE_SERVICE_ACCOUNT_JSON`** — base64-encoded service-account
  //   JSON. The prod shape: env vars rather than mounted files, and
  //   base64 sidesteps shell / dotenv / dashboard mangling of the
  //   literal `\n` characters inside `private_key`.
  //
  // The loader in `packages/google/src/service-account-key.ts` prefers
  // `JSON` when both are set; failures inside the JSON branch do NOT
  // fall through to PATH (would mask typos in the prod secret).
  GOOGLE_SERVICE_ACCOUNT_PATH: z.string().min(1).optional(),
  GOOGLE_SERVICE_ACCOUNT_JSON: z.string().min(1).optional(),

  // Google Sheets workbook ID (the chunk between `/d/` and `/edit` in
  // the workbook URL). Required by every consumer that talks to Sheets
  // (the curator agent, the bootstrap CLI). Validated at boot via
  // `loadEnv()` so misconfiguration surfaces before the first API call.
  SHEETS_WORKBOOK_ID: z.string().min(1),

  // Google Drive folder ID for the Curator's run-by-run reports. The
  // operator pre-creates `Operational/Database Exports/` in Drive,
  // shares it with the service account as Editor, and pastes the
  // folder ID here. The Curator uploads
  // `DB-CURATOR-REPORT-YYYY-MM-DD.doc` into this folder at the end of
  // every run. Same `drive.file` scope-only pattern as the Sheets
  // workbook — see `packages/google/src/drive-auth.ts`.
  DB_EXPORTS_FOLDER_ID: z.string().min(1),

  // Google Drive parent folder ID for the Consultant's per-engagement
  // client folders (issue #204). The orchestrator creates
  // `~/clients/{client_name}_{client_id}/` inside this folder on the
  // first confirmation per `client_id` and uploads the rendered
  // problem memo as a native Google Doc into it. Same Shared Drive
  // posture as `DB_EXPORTS_FOLDER_ID` — the SA has zero personal
  // Drive quota, so the parent MUST live inside a Shared Drive
  // pre-shared with the SA as Editor.
  //
  // Optional: when unset, the orchestrator skips DriveClient wiring
  // and confirmations land the `problem_memos` row + audit without
  // attempting an upload. This keeps local dev that doesn't touch
  // the public-chat surface buildable without a real Drive folder.
  // The `app.ts`-level invariant treats this as paired with the
  // (internal) DriveClient — either both wired or neither.
  CLIENTS_DRIVE_FOLDER_ID: z.string().min(1).optional(),

  // Postgres connection string (local Supabase defaults to port 54322).
  // Always the direct connection (port 5432 in prod), never the
  // transaction pooler (6543) — the orchestrator relies on connecting as
  // a `BYPASSRLS` role and PgBouncer in transaction mode breaks that.
  DATABASE_URL: z.string().url(),

  // Public base URL (admin UI, callback URLs). Named with the
  // `PUBLIC_` prefix to dodge Vite's reserved `BASE_URL` env var,
  // which Vite forces to `/` inside the Vitest runtime (issue #125).
  PUBLIC_BASE_URL: z.string().url(),

  // Salt used to hash per-agent secrets in the agents registry.
  AGENT_SECRET_SALT: z.string().min(1),

  // HMAC secret used to sign short-lived SSE stream tickets. The admin
  // UI fetches a ticket via `POST /api/jobs/:id/stream-ticket` (JWT-
  // gated), then opens `EventSource(/api/jobs/:id/stream?ticket=...)`.
  // EventSource cannot send headers, and stuffing the long-lived
  // Supabase JWT into the URL would log it on every reverse proxy on
  // the path; tickets are scoped to one (jobId, userId) pair and
  // expire in ~60s. Generate with `openssl rand -base64 48`.
  ORCHESTRATOR_STREAM_TICKET_SECRET: z.string().min(32),

  // Public Supabase project URL (e.g. `https://<ref>.supabase.co`, or
  // `http://localhost:54321` against the local CLI). The orchestrator
  // builds its JWKS resolver from `${SUPABASE_URL}/auth/v1/.well-known/jwks.json`
  // at boot — Supabase issues ES256 access tokens, and the verifier
  // fetches the project's public keys to check the signature. There is
  // no shared secret to manage; key rotation is automatic via the
  // in-memory JWKS cache. See `docs/orchestrator-auth.md`.
  SUPABASE_URL: z.string().url(),

  // Supabase anon (public) key. Intended-public — RLS-with-no-policies
  // means it grants no row access. Declared so the orchestrator boot
  // surfaces a misconfigured environment immediately rather than at
  // first use.
  SUPABASE_ANON_KEY: z.string().min(1),

  // The single CORS origin allowed to call the orchestrator from a
  // browser. Local: `http://localhost:5173` (admin-ui Vite dev server).
  // Prod: `https://app.prospect.solutions`. No wildcards — every other
  // origin is rejected by `@fastify/cors`.
  ADMIN_UI_ORIGIN: z.string().url(),

  // ─── M2 public auth (issue #135) ────────────────────────────────────
  //
  // HS256 secret used to sign the public-chatbot session JWT carried in
  // the `__Host-prospect_chat_session` cookie. Distinct from
  // `ORCHESTRATOR_STREAM_TICKET_SECRET` — the .refine() chained at the
  // bottom of this schema enforces that the two never collide, so a
  // leaked stream ticket cannot ever masquerade as a session token even
  // if the issuer/audience claim checks somehow regressed.
  //
  // Generate with `openssl rand -base64 48`. See `docs/public-auth.md`
  // for the cookie posture and `services/orchestrator/src/auth/public-session.ts`
  // for the claim shape.
  PUBLIC_SESSION_TICKET_SECRET: z.string().min(32),

  // Comma-separated allowlist of origins permitted to call
  // `/api/public/*` from a browser. Local:
  // `http://localhost:5174` (chatbot widget dev server). Prod:
  // `https://prospect.solutions,https://www.prospect.solutions` — the
  // widget is a same-origin `/chat/` embed on the marketing site
  // (ratified in PR #666 § "Part C" — no `chat.*` subdomain). Parsed
  // identically to `CLOUD_RUN_AGENT_AUDIENCE_ALLOWLIST` — trimmed,
  // empties dropped. Empty/unset values would resolve to "no public
  // origin is allowed", which fails closed but breaks the chatbot —
  // hence `.min(1)`.
  PUBLIC_CHATBOT_ORIGINS: z.string().min(1),

  // Base URL of the public chatbot widget (no trailing slash). Rendered
  // into the `code-delivery` Resend email as the "Open chatbot" CTA.
  // Local: `http://localhost:5174`. Prod: `https://prospect.solutions/chat`
  // — same-origin `/chat/` path on the marketing site (see PR #666 §
  // "Part C" for the deployment-shape decision). The 6-digit code is
  // NEVER appended as a query parameter to this URL (browser-history /
  // referrer leak defence — see the template docstring). Set as a
  // top-level env var so the email template stays pure and the
  // orchestrator's runtime configuration carries the URL.
  PUBLIC_CHATBOT_BASE_URL: z.string().url(),

  // HITL deadline knobs. The platform has two independent review flows
  // and each carries its own deadline pair so dev/staging environments
  // can shorten one for testing without silently expiring the other
  // (issue #578).
  //
  // - **`_JOB`** — M1 curator-draft review flow, backing
  //   `jobs.review_deadline`. Consumed by `enterPendingReview`
  //   (`applyAgentOutput`) and the job-side arm of `ReviewSweeper`.
  // - **`_PRESENTATION`** — M2 solution-presentation review flow,
  //   backing `solution_presentations.review_deadline_at`. Consumed by
  //   `applyMatchValidatorOutcome` and the presentation-side arm of
  //   `ReviewSweeper`.
  //
  // Both pairs accept fractional values so dev can verify the full
  // review_ready → reminder → expired arc in minutes
  // (`REVIEW_DEADLINE_HOURS_JOB=0.05`) without waiting three days. The
  // schema-level refines below enforce `reminder < deadline` per pair
  // so a misconfigured value fails at boot rather than silently never
  // firing a reminder.
  //
  // **Deprecated aliases** `REVIEW_DEADLINE_HOURS` and
  // `REVIEW_REMINDER_HOURS_BEFORE_DEADLINE` are honoured for backward
  // compatibility: when a split-var is unset, the alias supplies its
  // default (see the `.transform()` at the bottom of this schema).
  // Setting an alias triggers a boot-time `console.warn` from
  // {@link loadEnv} — migrate to the split-vars per
  // `docs/hitl-review-deadlines.md`.
  REVIEW_DEADLINE_HOURS: z.coerce.number().positive().optional(),
  REVIEW_REMINDER_HOURS_BEFORE_DEADLINE: z.coerce.number().positive().optional(),
  REVIEW_DEADLINE_HOURS_JOB: z.coerce.number().positive().optional(),
  REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_JOB: z.coerce.number().positive().optional(),
  REVIEW_DEADLINE_HOURS_PRESENTATION: z.coerce.number().positive().optional(),
  REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_PRESENTATION: z.coerce.number().positive().optional(),

  // Wall-clock sweeper knobs (issue #209). The sweeper transitions
  // `running` jobs past `started_at + max_run_time_minutes +
  // JOB_WALL_CLOCK_GRACE_MINUTES` to `failed`. The grace absorbs
  // post-LLM-loop flush time (final `worker_result` write +
  // `run_complete` SSE emission + stream close) so a clean finish
  // that lands a few seconds late isn't punished. Defaults match
  // the proposal in #209: 60s sweep cadence (same as the HITL
  // sweeper) and 5-minute grace.
  JOB_WALL_CLOCK_SWEEPER_INTERVAL_MS: z.coerce.number().int().positive().default(60_000),
  JOB_WALL_CLOCK_GRACE_MINUTES: z.coerce.number().nonnegative().default(5),

  // SSE consumer idle-timeout (issue #645). Layered defence against
  // a quiet-server hang on the paired-hop / worker SSE stream — the
  // orchestrator's `for await (const event of parseSseStream(body))`
  // races each `next()` against this timer, resetting on every
  // arriving event. On timeout, the consumer breaks out and falls
  // through to `recoverOutputFromDb`; the wall-clock sweeper (#209 /
  // #640) is the last-resort net if that also comes back empty. See
  // `docs/agent-dispatch.md` § "Durability contract" for the layering.
  // Default 60s: typed `heartbeat` SSE events arrive every 15s per
  // the agent contract (issue #655), so 60s of silence — four missed
  // heartbeats — unambiguously indicates the server side has stopped
  // talking.
  SSE_READ_IDLE_TIMEOUT_MS: z.coerce.number().int().positive().default(60_000),

  // Cost-monitor sweeper cadence (issue #214). Every tick the sweeper
  // fans every `running` job's id through the cost-alert hook, which
  // sums `llm_usage.cost_usd` and (on breach) emails the operator
  // before the job lands somewhere terminal. The per-tick cost is
  // `(N running jobs) × (one sum aggregate + one Sheets read per
  // breaching job)`, so a sub-minute cadence is wasteful at M2 scale.
  // Default 5 minutes — the upper end of the issue's "2–5 min,
  // tunable" guidance. Drop to single-digit seconds for the dev /
  // smoke path where you want to see a breach surface within a tick
  // without waiting.
  JOB_COST_MONITOR_SWEEPER_INTERVAL_MS: z.coerce.number().int().positive().default(300_000),

  // ─── PII retention sweepers (issue #193) ─────────────────────────────
  //
  // Three in-process sweepers implementing the retention rules from
  // `docs/decisions/2026-05-19-pii-handling.md`. Each follows the
  // pattern of the other in-process sweepers above: tick cadence is
  // configurable via the `_INTERVAL_MS` knob; the policy duration is a
  // separate `_DAYS` knob so the window can be tuned independently of
  // the sweep frequency. Both knobs accept fractional values so dev /
  // smoke runs can verify the full lifecycle in seconds. The day knobs
  // are `nonnegative` (not `positive`) so a dev can set `0` to sweep
  // on every tick without an off-by-one fight.
  //
  // **`client_sessions` inactivity sweeper.** Transitions `active →
  // expired` and purges `conversation_history` in the same UPDATE.
  // Default window 7 days per the M2 public-auth design — sessions
  // older than that lose their resume eligibility (see
  // `findResumableSessionByEmail`'s 7-day cutoff), so the inactivity
  // expiry mirrors the visitor-facing semantic. Cadence default 60s
  // mirrors the other sweepers; sub-minute is wasteful at M2 scale.
  CLIENT_SESSIONS_INACTIVITY_SWEEPER_INTERVAL_MS: z.coerce
    .number()
    .int()
    .positive()
    .default(60_000),
  CLIENT_SESSIONS_INACTIVITY_DAYS: z.coerce.number().nonnegative().default(7),

  // **`run_log` retention sweeper.** Bulk DELETE of rows whose
  // `completed_at` is past the retention horizon. The PII bound is the
  // *absence* of the row (Decision 3); `llm_usage` is preserved
  // (Decision 4). Default cadence 1 hour — the PII decision suggested
  // hourly, and Consultant traffic at M2 scale is a few hundred rows
  // per active hour, so the DELETE stays cheap.
  RUN_LOG_RETENTION_SWEEPER_INTERVAL_MS: z.coerce
    .number()
    .int()
    .positive()
    .default(3_600_000),
  RUN_LOG_RETENTION_DAYS: z.coerce.number().nonnegative().default(30),

  // **`problem_memos` 2-year anonymisation sweeper.** UPDATE that
  // nulls `client_id` + `decision_maker` on memos past the horizon
  // (Decision 9). Idempotent via `client_id IS NOT NULL` in the WHERE
  // — once nulled, the row exits the sweep set. Default cadence 24h;
  // the horizon is 2 years so sub-day cadence buys nothing. Window
  // default 730 (2 × 365) follows Decision 1's 2-year recommendation
  // — flagged for Tomer review, override here when the call lands.
  MEMO_ANONYMISATION_SWEEPER_INTERVAL_MS: z.coerce
    .number()
    .int()
    .positive()
    .default(86_400_000),
  MEMO_ANONYMISATION_DAYS: z.coerce.number().nonnegative().default(730),

  // **`prospect.tools` Sheets → Postgres sync sweeper (issue #154).**
  // In-process safety-net loop that reconciles the `tools_and_services`
  // Sheet with the Postgres mirror. The primary trigger is the HITL-
  // commit fire-and-forget hook in `routes/review.ts`; this sweeper
  // catches direct Sheet edits and any commit-trigger failures. The
  // 24h default matches the ADR's staleness budget
  // (`docs/decisions/2026-04-21-tools-sync-direction.md` § "Staleness
  // budget"). Operators wanting strictly-punctual nightly syncs point
  // an external Cloud Scheduler cron at
  // `POST /api/internal/tools/sync` and can tune this down / disable
  // via env override; see `docs/tools-sync.md`.
  TOOLS_SYNC_SWEEPER_INTERVAL_MS: z.coerce
    .number()
    .int()
    .positive()
    .default(86_400_000),

  // ─── Token-pacer ITPM overrides (issue #38) ─────────────────────────
  //
  // Per-model overrides for the input-tokens-per-minute ceilings baked
  // into `DEFAULT_PACER_CONFIG` (packages/messaging/src/pacer.ts). The
  // defaults are Anthropic's tier-1 published limits; when the org tier
  // moves up (or Anthropic re-tiers a model), set the matching env var
  // instead of editing the constant and redeploying.
  //
  // Each is OPTIONAL: unset = the tier-1 default for that model stays in
  // force (the escape hatch is additive, not a behaviour change). When
  // set, `loadPacerConfig` overlays the value onto that model's bucket
  // `itpm`; `targetUtilization` is not overridable here.
  //
  // A malformed value (non-integer, ≤ 0, non-numeric) fails-fast at boot
  // via `parseEnv` — it never silently falls back to the default, so a
  // fat-fingered ceiling surfaces immediately rather than at the first
  // 429. (Issue #38 risk note: "do NOT silently fall back to defaults on
  // parse failure.")
  PACER_HAIKU_4_5_ITPM: z.coerce.number().int().positive().optional(),
  PACER_SONNET_4_6_ITPM: z.coerce.number().int().positive().optional(),
  PACER_OPUS_4_7_ITPM: z.coerce.number().int().positive().optional(),

  // Email transport (consumed only inside packages/email/). `EMAIL_FROM`
  // is required and not defaulted — defaulting a deliverability-critical
  // value to a domain that may not be verified in Resend would silently
  // break sends in prod. `EMAIL_REPLY_TO` is optional and surfaces in
  // the recipient's reply-to header if set.
  //
  // Admin recipients (`review_ready`, reminder, cost alert) live in the
  // workbook's `agent_config.notify_email` cell, not in env. The
  // orchestrator reads them on every send via
  // `lib/review-recipients.ts#createSheetsRecipientResolver`.
  EMAIL_FROM: z.string().min(1),
  EMAIL_REPLY_TO: z.string().email().optional(),

  // ─── Rate limiting (issue #178) ─────────────────────────────────────
  //
  // Outer per-IP fence registered via `@fastify/rate-limit` at the
  // orchestrator's root, layered on top of the existing custom
  // limiters in `services/orchestrator/src/auth/public-rate-limit.ts`
  // (brute-force on /redeem) and
  // `services/orchestrator/src/auth/public-chat-rate-limit.ts`
  // (token bucket on /chat/messages). The custom limiters stay — they
  // implement specialised semantics (lockout, agent_config-driven live
  // tuning) that the broad fence does not.
  //
  // One bucket per scope, keyed `<scope>:<request.ip>`. Window fixed
  // at 1 minute; defaults sized so a real human or a known caller
  // never trips them but a brute-force loop does. Tunable in prod by
  // editing Cloud Run env (no redeploy). Single-instance Cloud Run
  // posture is baked in — state is in-process, lost on instance
  // recycle; see `docs/decisions/2026-06-14-orchestrator-rate-limiting.md`
  // for the scale-out plan.
  RATE_LIMIT_HEALTH_PER_MIN: z.coerce.number().int().positive().default(30),
  RATE_LIMIT_ADMIN_PER_MIN: z.coerce.number().int().positive().default(300),
  RATE_LIMIT_INTERNAL_PER_MIN: z.coerce.number().int().positive().default(60),
  // Callback default sized for a small fleet of Cloud Run agents
  // sharing a single egress NAT IP. The default 240/min (≈ 4/sec
  // sustained from one source) gives headroom over the M2 dispatch
  // shape (curator + validator each completing a few jobs/min) and
  // protects against a misbehaving agent looping.
  RATE_LIMIT_CALLBACKS_PER_MIN: z.coerce.number().int().positive().default(240),
  RATE_LIMIT_STREAM_PER_MIN: z.coerce.number().int().positive().default(30),
  RATE_LIMIT_TELEGRAM_PER_MIN: z.coerce.number().int().positive().default(60),
  RATE_LIMIT_PUBLIC_PER_MIN: z.coerce.number().int().positive().default(60),

  // Cloud Run ID-token audience allowlist. Comma-separated list of full
  // origins (scheme + host, no path), e.g.
  // `https://prospect-curator-1234567.europe-west2.run.app,https://prospect-validator-7654321.europe-west2.run.app`.
  // The orchestrator's dispatcher consults this set before fetching a
  // Google-signed ID token for any `*.run.app` endpoint URL it reads
  // from `agents.endpoint_url`. Fail-closed semantics: if the URL
  // passes `isCloudRunUrl` but its origin isn't in this list, the
  // dispatch throws a non-retryable `missing_secret` error rather
  // than minting a token bound to an attacker-controlled audience.
  //
  // Optional so dev/local-only deploys (no Cloud Run endpoints) don't
  // have to set it — `isCloudRunUrl` already short-circuits non-Cloud-Run
  // URLs. Unset is equivalent to an empty allowlist: every Cloud Run
  // dispatch fails closed. See
  // `docs/decisions/2026-05-14-cloud-run-migration.md` for the rationale
  // and issue #166 for the threat model.
  CLOUD_RUN_AGENT_AUDIENCE_ALLOWLIST: z.string().min(1).optional(),

  // ─── Telegram bridge (issue #270) ─────────────────────────────────────
  //
  // The cloud routine posts `[automation:*]` comments on GitHub; the
  // GHA workflow `.github/workflows/routine-notify.yml` forwards them to
  // Telegram with inline buttons. Inbound clicks + replies hit
  // `POST /api/telegram/webhook` on the orchestrator, which translates
  // them into GitHub label changes / comments. See
  // `docs/routine-notifications.md` § Phase 2.
  //
  // All four are optional at the schema layer so non-prod environments
  // (and any future deploy that doesn't run the bridge) can omit them
  // — `app.ts` skips registering `telegramRoutes` when any of the four
  // is missing. Partial configuration is treated as "bridge off"
  // rather than a boot failure; the omission has to be deliberate per
  // environment. Production sets all four via Secret Manager + plain
  // env values in `service.prod.yaml`.

  // Bot token from BotFather. Shared with the GHA workflow secret of
  // the same name — same token, two consumers (the workflow sends the
  // outbound message; the orchestrator route handles inbound
  // callback_query + sends edit/answerCallback/reply calls).
  TELEGRAM_BOT_TOKEN: z.string().min(1).optional(),

  // Shared secret passed to Telegram's `setWebhook?secret_token=…` and
  // verified on every inbound webhook hit via the
  // `X-Telegram-Bot-Api-Secret-Token` header. The route does a
  // constant-time compare and 401s on mismatch. Distinct from the
  // bot token so a leaked token alone cannot impersonate inbound
  // webhook calls. Generate with `openssl rand -hex 32`. Min 32
  // matches the other HMAC-style secrets in this schema.
  TELEGRAM_WEBHOOK_SECRET: z.string().min(32).optional(),

  // GitHub PAT scoped to `repo` on the bridge-target repo. Used by
  // the orchestrator for label-add and comment-post calls when an
  // inbound webhook acts on an issue. Issue from github.com →
  // Settings → Developer settings → Tokens (classic) and treat as
  // a long-lived secret — rotate alongside the webhook secret on
  // any leak.
  TELEGRAM_BRIDGE_GH_PAT: z.string().min(1).optional(),

  // The single repo the bridge is allowed to write to, in
  // `owner/repo` form (e.g. `PROSPECT-TECH-LTD/prospect-platform`).
  // Belt-and-braces against a misrouted callback or a compromised
  // PAT scope writing to a repo the bridge isn't supposed to touch.
  TELEGRAM_BRIDGE_REPO: z
    .string()
    .regex(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/, "must be in owner/repo form")
    .optional(),

  // Comma-separated allowlist of Telegram chat IDs permitted to drive
  // bridge actions. Today the bot is private to Olly's DM, so the
  // list is a single integer. Fail-closed: when unset OR empty, every
  // inbound `callback_query` and reply-message is rejected with an
  // `unauthorized` audit row. This caps the blast radius if the bot
  // username is discovered and a third party DMs it — they can
  // authenticate the webhook with the secret token (no, that's
  // bot-side, not chat-side), but their `chat_id` won't match.
  // Parsed at boot in `index.ts` into a `Set<number>` and threaded
  // into the route deps.
  TELEGRAM_ALLOWED_CHAT_IDS: z.string().min(1).optional(),

  // Issue reporter — admin-ui "Report" tab (issue #287). When set,
  // the orchestrator mounts `POST /api/admin/report-issue/messages`
  // and the file-issue tool's handler hits the GitHub REST API with
  // this PAT. Fine-grained PAT scoped to `Issues: read and write`
  // on the target repo. Optional — when unset the route is not
  // mounted; admin-ui's first send attempt receives a generic
  // error (the dedicated "not configured" affordance is a
  // follow-up — see `docs/report-issue.md` § Follow-ups).
  GITHUB_REPORTER_PAT: z.string().min(1).optional(),

  // The single repo the issue reporter is allowed to write to, in
  // `owner/repo` form. Belt-and-braces against a malformed config:
  // even if `GITHUB_REPORTER_PAT` granted access to extra repos, the
  // route only ever posts here. Defaults at the boot site (`index.ts`)
  // to `PROSPECT-TECH-LTD/prospect-platform` when omitted while the
  // PAT is set.
  GITHUB_REPORTER_REPO: z
    .string()
    .regex(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/, "must be in owner/repo form")
    .optional(),

  // Optional / defaulted.
  LOG_LEVEL: z.enum(["trace", "debug", "info", "warn", "error"]).default("info"),
  TEST_DATABASE_URL: z.string().url().optional(),
}).refine(
  (env) => resolveJobReminder(env) < resolveJobDeadline(env),
  {
    path: ["REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_JOB"],
    message:
      "REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_JOB must be strictly less than REVIEW_DEADLINE_HOURS_JOB (or the deprecated REVIEW_REMINDER_HOURS_BEFORE_DEADLINE / REVIEW_DEADLINE_HOURS fallback pair)",
  },
).refine(
  (env) => resolvePresentationReminder(env) < resolvePresentationDeadline(env),
  {
    path: ["REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_PRESENTATION"],
    message:
      "REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_PRESENTATION must be strictly less than REVIEW_DEADLINE_HOURS_PRESENTATION (or the deprecated REVIEW_REMINDER_HOURS_BEFORE_DEADLINE / REVIEW_DEADLINE_HOURS fallback pair)",
  },
).refine(
  // Both fields use `.min(1).optional()`, so empty strings already fail
  // field-level validation and never reach this refine — `!== undefined`
  // is sufficient here.
  (env) =>
    env.GOOGLE_SERVICE_ACCOUNT_PATH !== undefined ||
    env.GOOGLE_SERVICE_ACCOUNT_JSON !== undefined,
  {
    path: ["GOOGLE_SERVICE_ACCOUNT_PATH"],
    message:
      "Set either GOOGLE_SERVICE_ACCOUNT_PATH (local-dev file path) or " +
      "GOOGLE_SERVICE_ACCOUNT_JSON (prod base64-encoded JSON).",
  },
).refine(
  // Defence-in-depth against cross-scheme replay. The session JWT and
  // the stream-ticket JWT differ on `iss` + `aud` claims, so a leaked
  // ticket already fails to verify in the other scheme's plugin. The
  // shared-secret check below makes the failure mode "verifies with
  // the wrong key", not "verifies with the right key but trips on
  // claim mismatch" — a clearer log signal during incident response.
  (env) => env.PUBLIC_SESSION_TICKET_SECRET !== env.ORCHESTRATOR_STREAM_TICKET_SECRET,
  {
    path: ["PUBLIC_SESSION_TICKET_SECRET"],
    message:
      "PUBLIC_SESSION_TICKET_SECRET must differ from ORCHESTRATOR_STREAM_TICKET_SECRET",
  },
).refine(
  // Defence-in-depth against re-use across schemes. The Telegram
  // webhook secret is presented at a different audience (Telegram
  // POSTs the value in a request header) and rotates independently
  // from the JWT-signing secrets above, so accidental sharing of one
  // value across all three is a real footgun. Enforce non-collision
  // at boot rather than discovering at the wire.
  //
  // Only fires when the bridge is actually configured — staging
  // environments without the bridge wired skip the check (their
  // `TELEGRAM_WEBHOOK_SECRET` is undefined).
  (env) =>
    env.TELEGRAM_WEBHOOK_SECRET === undefined ||
    (env.TELEGRAM_WEBHOOK_SECRET !== env.ORCHESTRATOR_STREAM_TICKET_SECRET &&
      env.TELEGRAM_WEBHOOK_SECRET !== env.PUBLIC_SESSION_TICKET_SECRET),
  {
    path: ["TELEGRAM_WEBHOOK_SECRET"],
    message:
      "TELEGRAM_WEBHOOK_SECRET must differ from ORCHESTRATOR_STREAM_TICKET_SECRET and PUBLIC_SESSION_TICKET_SECRET",
  },
).transform((env) => ({
  // Resolve the split HITL deadline vars from either the explicit
  // split-var or the deprecated single-var alias, falling back to the
  // production defaults (72h deadline, 24h reminder offset) when
  // neither is set. Consumers see stable field names regardless of
  // which the operator provided.
  ...env,
  REVIEW_DEADLINE_HOURS_JOB: resolveJobDeadline(env),
  REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_JOB: resolveJobReminder(env),
  REVIEW_DEADLINE_HOURS_PRESENTATION: resolvePresentationDeadline(env),
  REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_PRESENTATION:
    resolvePresentationReminder(env),
}));

// ─── HITL deadline resolvers (issue #578) ────────────────────────────────
//
// The refines and the transform above both need to compute the "effective"
// value of each split var given the deprecated aliases. Hoisted to
// module scope so both call sites resolve identically — a subtle
// divergence would let a misconfigured pair pass the refine and then
// mis-fire the reminder at runtime.

const DEFAULT_DEADLINE_HOURS = 72;
const DEFAULT_REMINDER_HOURS = 24;

interface DeadlineVarsSource {
  REVIEW_DEADLINE_HOURS?: number;
  REVIEW_REMINDER_HOURS_BEFORE_DEADLINE?: number;
  REVIEW_DEADLINE_HOURS_JOB?: number;
  REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_JOB?: number;
  REVIEW_DEADLINE_HOURS_PRESENTATION?: number;
  REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_PRESENTATION?: number;
}

function resolveJobDeadline(env: DeadlineVarsSource): number {
  return (
    env.REVIEW_DEADLINE_HOURS_JOB ??
    env.REVIEW_DEADLINE_HOURS ??
    DEFAULT_DEADLINE_HOURS
  );
}

function resolveJobReminder(env: DeadlineVarsSource): number {
  return (
    env.REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_JOB ??
    env.REVIEW_REMINDER_HOURS_BEFORE_DEADLINE ??
    DEFAULT_REMINDER_HOURS
  );
}

function resolvePresentationDeadline(env: DeadlineVarsSource): number {
  return (
    env.REVIEW_DEADLINE_HOURS_PRESENTATION ??
    env.REVIEW_DEADLINE_HOURS ??
    DEFAULT_DEADLINE_HOURS
  );
}

function resolvePresentationReminder(env: DeadlineVarsSource): number {
  return (
    env.REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_PRESENTATION ??
    env.REVIEW_REMINDER_HOURS_BEFORE_DEADLINE ??
    DEFAULT_REMINDER_HOURS
  );
}

export type Env = z.infer<typeof envSchema>;

/**
 * Parse a synthetic env object against `envSchema`. Pure — never reads or
 * caches `process.env`. Throws an `Error` whose message lists every
 * offending var on a separate line.
 *
 * Use this for tests, scripts, or any caller that wants to validate an
 * arbitrary input. Production code should call {@link loadEnv} instead so
 * the parsed result is memoised across the process.
 */
export function parseEnv(source: NodeJS.ProcessEnv): Env {
  const result = envSchema.safeParse(source);
  if (!result.success) {
    const details = result.error.issues
      .map((issue) => `  - ${issue.path.join(".")}: ${issue.message}`)
      .join("\n");
    throw new Error(`Invalid environment variables:\n${details}`);
  }
  return result.data;
}

let cached: Env | undefined;

/**
 * Parse `process.env` once and return the typed, validated env. The first
 * call is the boot moment — if any required var is missing or malformed,
 * it throws via {@link parseEnv} so failures surface immediately. All
 * subsequent calls return the cached value without re-parsing.
 *
 * Emits a `console.warn` on the first-call path if either deprecated
 * HITL deadline alias (`REVIEW_DEADLINE_HOURS` /
 * `REVIEW_REMINDER_HOURS_BEFORE_DEADLINE`) is set, prompting migration
 * to the split-vars.
 */
export function loadEnv(): Env {
  if (!cached) {
    cached = parseEnv(process.env);
    warnOnDeprecatedReviewDeadlineAliases(process.env);
  }
  return cached;
}

function warnOnDeprecatedReviewDeadlineAliases(raw: NodeJS.ProcessEnv): void {
  if (raw.REVIEW_DEADLINE_HOURS !== undefined) {
    console.warn(
      "REVIEW_DEADLINE_HOURS is deprecated — migrate to " +
        "REVIEW_DEADLINE_HOURS_JOB and REVIEW_DEADLINE_HOURS_PRESENTATION. " +
        "The old value is still applied as a fallback default to each " +
        "unset split-var (issue #578).",
    );
  }
  if (raw.REVIEW_REMINDER_HOURS_BEFORE_DEADLINE !== undefined) {
    console.warn(
      "REVIEW_REMINDER_HOURS_BEFORE_DEADLINE is deprecated — migrate to " +
        "REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_JOB and " +
        "REVIEW_REMINDER_HOURS_BEFORE_DEADLINE_PRESENTATION. " +
        "The old value is still applied as a fallback default to each " +
        "unset split-var (issue #578).",
    );
  }
}

/**
 * Test-only: drop the cached env so the next {@link loadEnv} call re-parses.
 * Not exported through the package barrel — tests import this from
 * `@prospect/shared/env` (or a relative path) deliberately, so production
 * code cannot reach it by accident.
 */
export function _resetEnvCacheForTests(): void {
  cached = undefined;
}
